/**
 * FreemiumGate — non-blocking gate shown when the points quota is exceeded.
 *
 * Shows remaining points and upgrade CTA (Mini or Premium).
 */

import { Crown, Zap, X } from 'lucide-react';
import { Modal } from './Modal';
import { getPointsSummary, ACTION_COSTS, type ActionType } from '../../lib/credits-store';
import { useI18n } from '../../contexts/I18nContext';

/** @deprecated kept for backward compat with App.tsx — maps to ActionType */
export type CreditType = 'aiAnalysis' | 'cvGeneration' | 'coverLetter';

const ACTION_MAP: Record<CreditType, ActionType> = {
  aiAnalysis: 'aiAnalysis',
  cvGeneration: 'optimize',
  coverLetter: 'coverLetter',
};

interface FreemiumGateProps {
  open: boolean;
  onClose: () => void;
  action: CreditType;
  onUpgrade: () => void;
}

export function FreemiumGate({ open, onClose, action, onUpgrade }: FreemiumGateProps) {
  const { t } = useI18n();
  const summary = getPointsSummary();
  const cost = ACTION_COSTS[ACTION_MAP[action]];
  const label = t(`freemiumGate.action${action.charAt(0).toUpperCase()}${action.slice(1)}`);

  const PREMIUM_FEATURES = [
    t('freemiumGate.feature1'),
    t('freemiumGate.feature2'),
    t('freemiumGate.feature3'),
    t('freemiumGate.feature4'),
    t('freemiumGate.feature5'),
    t('freemiumGate.feature6'),
  ];

  return (
    <Modal open={open} onClose={onClose} size="md">
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
            <Crown className="size-5 text-amber-400" />
          </div>
          <div>
            <h2 className="text-base font-bold text-white">{t('freemiumGate.title')}</h2>
            <p className="text-sm text-white/50 mt-0.5">
              {t('freemiumGate.subtitle')
                .replace('{cost}', String(cost))
                .replace('{remaining}', String(summary.remaining ?? '∞'))}
              {' '}{label}
            </p>
          </div>
        </div>

        {/* Points bar */}
        {summary.totalAvailable !== null && (
          <div className="space-y-1.5">
            <div className="flex justify-between text-[10px] text-white/40">
              <span>{t('freemiumGate.creditsUsed')}</span>
              <span>{summary.spent} / {summary.totalAvailable} pts</span>
            </div>
            <div className="h-2 rounded-full bg-white/8 overflow-hidden">
              <div
                className="h-full rounded-full bg-red-500 transition-all"
                style={{ width: `${summary.usagePercent}%` }}
              />
            </div>
            <p className="text-[10px] text-white/30">{t('freemiumGate.renewsAuto')}</p>
          </div>
        )}

        {/* Premium features */}
        <div className="rounded-xl bg-gradient-to-br from-amber-500/10 to-orange-500/5 border border-amber-500/20 p-4 space-y-3">
          <p className="text-sm font-bold text-amber-300 flex items-center gap-2">
            <Zap className="size-4" /> {t('freemiumGate.premiumTitle')}
          </p>
          <ul className="space-y-1.5">
            {PREMIUM_FEATURES.map((f) => (
              <li key={f} className="flex items-center gap-2 text-xs text-white/70">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                {f}
              </li>
            ))}
          </ul>
        </div>

        {/* CTA */}
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => { onUpgrade(); onClose(); }}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-bold text-sm transition-all shadow-lg shadow-amber-500/20"
          >
            <Crown className="size-4" />
            {t('freemiumGate.seeOffers')}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2.5 rounded-xl text-xs text-white/40 hover:text-white/60 transition-colors flex items-center justify-center gap-1.5"
          >
            <X className="size-3.5" />
            {t('common.close')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
